<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item :to="{
              name: 'DashboardHome',
            }">
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item
              :to="{
                name: 'SystemSettingsHome',
              }"
            >全系統管理</b-breadcrumb-item>
            <b-breadcrumb-item
              :to="{
                name: 'SystemSuiteList',
              }"
            >系統方案管理</b-breadcrumb-item>
            <b-breadcrumb-item active>{{ getPageTitle() }}</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <h4 class="mb-2 mb-xl-0 mr-2 font-weight-bold">{{ getPageTitle() }}</h4>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <div
          class="col-12 col-xl-5 d-flex flex-column flex-xl-row align-items-start align-items-xl-center ml-auto"
        >
          <b-button
            v-if="checkPermission([consts.SUITE_PACKAGE_CREATE])"
            class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
            variant="primary"
            @click="handleCreate"
          >
            <i class="fa fa-plus"></i>新增方案套件
          </b-button>
          <b-input-group>
            <b-form-input placeholder="搜尋方案套件type或key" v-model="query.keyword" @keyup.enter="handlePageResetWithoutReload"></b-form-input>
            <b-input-group-append>
              <b-button @click="handlePageResetWithoutReload">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>

      <b-table striped hover responsive :items="suitePackages" :fields="fields" :busy="isLoading">
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="checkPermission([consts.SUITE_PACKAGE_LIST_VIEW])"
            class="ml-2"
            variant="inverse-primary"
            :to="{
              name: 'SuitePackageShow',
              params: {
                suiteId: suiteId,
                suitePackageId: data.item.id,
              },
            }"
          ><span class="mdi mdi-eye"></span> 查看</b-button>

          <b-button
            v-if="checkPermission([consts.SUITE_PACKAGE_MODIFY])"
            class="ml-2"
            variant="inverse-warning"
            :to="{
              name: 'SuitePackageEdit',
              params: {
                suiteId: suiteId,
                suitePackageId: data.item.id,
              },
            }"
          ><span class="mdi mdi-lead-pencil"></span> 編輯</b-button>
          <span :id="`delete-btn-${data.item.id}`" v-if="checkPermission([consts.SUITE_PACKAGE_DELETE])">
            <b-button
              class="ml-2"
              variant="inverse-danger"
              @click="handleDelete(data.item.id, data.item.key, data.item.type)"
            ><span class="mdi mdi-delete"></span> 刪除</b-button>
          </span>
        </template>
      </b-table>
      <CustomPagination
        :currentPage="query.page"
        :totalRows="total"
        :perPage="query.per_page"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import suiteApi from "../../../apis/suite";
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { paginationMixin } from "@/mixins/pagination";

export default {
  components: { CustomPagination },
  mixins: [ paginationMixin ],
  data() {
    return {
      consts,
      isLoading: false,
      suitePackages: [],
      suite: null,
      fields: [
        {
          key: "type",
          label: "Type",
        },
        {
          key: "key",
          label: "Key",
        },
        {
          key: "target",
          label: "Target",
        },
        {
          key: "table",
          label: "Table",
        },
        {
          key: "actions",
          label: "管理",
        },
      ],
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        keyword: "",
      },
      total: 0,
    };
  },
  computed: {
    ...mapGetters("route", ["routeQuery"]),
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    suiteId() {
      return this.$route.params.suiteId;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrlWithoutReload(this.query, this.$store, this.$router);
      },
    },
    routeQuery() {
      if (!this.initialized) return;
      this.fetchSuitePackages();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {
    if (!this.checkPermission([consts.SUITE_PACKAGE_LIST_VIEW])) {
      this.$swal
        .fire({
          type: "error",
          text: "你沒有權限訪問此頁面",
          confirmButtonColor: "#d33"
        })
        .then(() => {
          this.$router.push({ name: "SystemSuiteList" });
        });
      return;
    }
    await this.fetchSuitePackages();
    this.initialized = true;
  },
  methods: {
    async fetchSuitePackages() {
      try {
        this.isLoading = true;
        let params = {...this.query}
        const { data } = await suiteApi.getSuitePackages(this.suiteId, params);
        this.total = data.meta.total;
        this.suitePackages = data.data;
        this.suite = data.suite;
      } catch (error) {
        console.log("");
      }
      this.isLoading = false;
    },
    getPageTitle() {
      return this.suite ? `${this.suite.name} - 方案套件列表` : '方案套件列表';
    },
    async handleDelete(suitePackageId, key, type) {
      if (!this.checkPermission([consts.SUITE_PACKAGE_DELETE])) {
        this.$swal.fire({
          type: "error",
          text: "你沒有權限刪除此資料",
          confirmButtonColor: "#d33"
        });
        return;
      }

      this.$swal
        .fire({
          type: "warning",
          title: "你確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>type：${type}</div>
                <div>key：${key}</div>
              </div>
              <div>刪除後，相關資料將無法回復，<br/>請您確認！</div>
            </div>`,
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            try {
              this.isLoading = true;
              await suiteApi.deleteSuitePackage(this.suiteId, suitePackageId);

              this.$swal
                .fire({
                  type: "success",
                  text: `${key} 已成功刪除`,
                })
                .then(async () => {
                  // 重新載入資料
                  await this.fetchSuitePackages();
                });
            } catch (error) {
              console.error("刪除失敗:", error);

              // 顯示錯誤訊息
              this.$swal.fire("刪除失敗", error.response?.data?.message || error.message || "刪除方案套件時發生錯誤", "error");
            } finally {
              this.isLoading = false;
            }
          }
        });
    },
    handleCreate() {
      if (!this.checkPermission([consts.SUITE_PACKAGE_CREATE])) {
        this.$swal.fire({
          type: "error",
          text: "你沒有權限新增此資料",
          confirmButtonColor: "#d33"
        });
        return;
      }

      this.$router.push({
        name: "SuitePackageCreate",
        params: {
          suiteId: this.suiteId,
        },
      });
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    }
  },
};
</script>
